import { useState } from "react";
import useAuthStore from "@/stores/auth";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Copy,
  ChevronDown,
  ChevronRight,
  Download,
  Database,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "react-hot-toast";

interface StoreSection {
  name: string;
  description: string;
  getState: () => Record<string, unknown>;
  icon: React.ReactNode;
}

// Define stores to display - add more as needed
const STORE_SECTIONS: StoreSection[] = [
  {
    name: "Auth Store",
    description: "Authentication, user, business, and RBAC state",
    getState: () => {
      const state = useAuthStore.getState();
      // Exclude sensitive data like tokens from display
      return {
        user: state.user
          ? {
              id: state.user.id,
              email: state.user.email,
              firstName: state.user.firstName,
              lastName: state.user.lastName,
            }
          : null,
        business: state.business
          ? {
              id: state.business.id,
              name: state.business.name,
            }
          : null,
        isAuthenticated: state.isAuthenticated,
        loading: state.loading,
        error: state.error,
        // RBAC
        currentTeam: state.currentTeam,
        currentTeamId: state.currentTeamId,
        teams: state.teams,
        permissions: state.permissions,
        roles: state.roles,
        organizationRole: state.organizationRole,
        isOnboarded: state.isOnboarded,
        teamsLoading: state.teamsLoading,
        permissionsLoading: state.permissionsLoading,
        teamsError: state.teamsError,
        permissionsError: state.permissionsError,
        _hasHydrated: state._hasHydrated,
        // Tokens (masked)
        hasToken: !!state.token,
        hasRefreshToken: !!state.refreshToken,
      };
    },
    icon: <Database className="h-4 w-4" />,
  },
];

export function StateDebugTab() {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["Auth Store"])
  );
  const [refreshKey, setRefreshKey] = useState(0);

  const toggleSection = (name: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(name)) {
        newSet.delete(name);
      } else {
        newSet.add(name);
      }
      return newSet;
    });
  };

  const copyToClipboard = (data: unknown, label: string) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success(`${label} copied to clipboard`);
  };

  const exportAllState = () => {
    const allState: Record<string, unknown> = {};
    STORE_SECTIONS.forEach((section) => {
      allState[section.name] = section.getState();
    });

    // Add localStorage snapshot
    const storage: Record<string, unknown> = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        try {
          storage[key] = JSON.parse(localStorage.getItem(key) || "");
        } catch {
          storage[key] = localStorage.getItem(key);
        }
      }
    }
    allState["localStorage"] = storage;

    // Create and download file
    const blob = new Blob([JSON.stringify(allState, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `justping-debug-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success("Debug snapshot exported");
  };

  const refreshState = () => {
    setRefreshKey((prev) => prev + 1);
    toast.success("State refreshed");
  };

  return (
    <ScrollArea className="h-[calc(80vh-180px)]">
      <div className="space-y-4 pr-4" key={refreshKey}>
        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Live Zustand store snapshots
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={refreshState}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Refresh
            </Button>
            <Button variant="default" size="sm" onClick={exportAllState}>
              <Download className="h-3 w-3 mr-1" />
              Export All
            </Button>
          </div>
        </div>

        {/* Store Sections */}
        {STORE_SECTIONS.map((section) => {
          const state = section.getState();
          const isExpanded = expandedSections.has(section.name);

          return (
            <div
              key={section.name}
              className="border rounded-lg overflow-hidden"
            >
              {/* Header */}
              <div
                className="flex items-center justify-between p-3 bg-muted/30 cursor-pointer"
                onClick={() => toggleSection(section.name)}
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  {section.icon}
                  <div>
                    <div className="font-medium text-sm">{section.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {section.description}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {Object.keys(state).length} keys
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(state, section.name);
                    }}
                  >
                    <Copy className="h-3 w-3 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t">
                  <StateTreeView data={state} />
                </div>
              )}
            </div>
          );
        })}

        {/* Quick Info Cards */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <QuickInfoCard
            title="Auth Status"
            items={[
              {
                label: "Authenticated",
                value: useAuthStore.getState().isAuthenticated ? "Yes" : "No",
                status: useAuthStore.getState().isAuthenticated,
              },
              {
                label: "Hydrated",
                value: useAuthStore.getState()._hasHydrated ? "Yes" : "No",
                status: useAuthStore.getState()._hasHydrated,
              },
              {
                label: "Team Selected",
                value: useAuthStore.getState().currentTeam ? "Yes" : "No",
                status: !!useAuthStore.getState().currentTeam,
              },
            ]}
          />
          <QuickInfoCard
            title="Loading States"
            items={[
              {
                label: "Auth Loading",
                value: useAuthStore.getState().loading ? "Yes" : "No",
                status: !useAuthStore.getState().loading,
              },
              {
                label: "Teams Loading",
                value: useAuthStore.getState().teamsLoading ? "Yes" : "No",
                status: !useAuthStore.getState().teamsLoading,
              },
              {
                label: "Permissions Loading",
                value: useAuthStore.getState().permissionsLoading ? "Yes" : "No",
                status: !useAuthStore.getState().permissionsLoading,
              },
            ]}
          />
        </div>
      </div>
    </ScrollArea>
  );
}

// Recursive tree view component
function StateTreeView({
  data,
  depth = 0,
}: {
  data: unknown;
  depth?: number;
}) {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  if (data === null) {
    return <span className="text-muted-foreground italic">null</span>;
  }

  if (data === undefined) {
    return <span className="text-muted-foreground italic">undefined</span>;
  }

  if (typeof data === "boolean") {
    return (
      <span className={data ? "text-green-600" : "text-red-600"}>
        {String(data)}
      </span>
    );
  }

  if (typeof data === "number") {
    return <span className="text-blue-600">{data}</span>;
  }

  if (typeof data === "string") {
    if (data.length > 100) {
      return (
        <span className="text-orange-600">"{data.slice(0, 100)}..."</span>
      );
    }
    return <span className="text-orange-600">"{data}"</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className="text-muted-foreground">[]</span>;
    }

    return (
      <div className="pl-4">
        {data.map((item, index) => (
          <div key={index} className="flex items-start gap-1">
            <span className="text-muted-foreground shrink-0">[{index}]:</span>
            <StateTreeView data={item} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }

  if (typeof data === "object") {
    const entries = Object.entries(data);

    if (entries.length === 0) {
      return <span className="text-muted-foreground">{"{}"}</span>;
    }

    return (
      <div className={cn(depth > 0 && "pl-4")}>
        {entries.map(([key, value]) => {
          const isExpandable =
            typeof value === "object" && value !== null;
          const isExpanded = expandedKeys.has(key);
          const shouldAutoExpand = depth < 2;

          return (
            <div key={key} className="py-0.5">
              <div
                className={cn(
                  "flex items-start gap-1",
                  isExpandable && "cursor-pointer hover:bg-muted/50 rounded"
                )}
                onClick={() => {
                  if (isExpandable) {
                    setExpandedKeys((prev) => {
                      const newSet = new Set(prev);
                      if (newSet.has(key)) {
                        newSet.delete(key);
                      } else {
                        newSet.add(key);
                      }
                      return newSet;
                    });
                  }
                }}
              >
                {isExpandable && (
                  <span className="shrink-0 text-muted-foreground">
                    {isExpanded || shouldAutoExpand ? (
                      <ChevronDown className="h-3 w-3 inline" />
                    ) : (
                      <ChevronRight className="h-3 w-3 inline" />
                    )}
                  </span>
                )}
                <span className="text-purple-600 shrink-0">{key}:</span>
                {!isExpandable && (
                  <StateTreeView data={value} depth={depth + 1} />
                )}
                {isExpandable && !isExpanded && !shouldAutoExpand && (
                  <span className="text-muted-foreground text-xs">
                    {Array.isArray(value)
                      ? `[${value.length} items]`
                      : `{${Object.keys(value).length} keys}`}
                  </span>
                )}
              </div>
              {isExpandable && (isExpanded || shouldAutoExpand) && (
                <StateTreeView data={value} depth={depth + 1} />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  return <span>{String(data)}</span>;
}

// Quick info card component
function QuickInfoCard({
  title,
  items,
}: {
  title: string;
  items: { label: string; value: string; status: boolean }[];
}) {
  return (
    <div className="border rounded-lg p-3">
      <div className="font-medium text-sm mb-2">{title}</div>
      <div className="space-y-1">
        {items.map((item) => (
          <div
            key={item.label}
            className="flex items-center justify-between text-xs"
          >
            <span className="text-muted-foreground">{item.label}</span>
            <Badge
              variant={item.status ? "default" : "destructive"}
              className="text-xs h-5"
            >
              {item.value}
            </Badge>
          </div>
        ))}
      </div>
    </div>
  );
}
